/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { mono } from './monolith.tokens'

interface PropsType {
	show: boolean
	onClick: () => void
}

/**
 * 맨 위로 — 화면 우측 하단의 헤어라인 사각 하나.
 *
 * 예전 `atoms/GoTop` 은 검은 원(64px) + 그림자 + 굵은 MDI 아이콘 + "Top" 글자였다. 페이지 전체가
 * 헤어라인·Thin 타이포로 물러서 있는데 모서리의 검은 덩어리 하나가 가장 먼저 눈에 들어왔다
 * (PERSONA §1 "봐달라" 금지, §2 액센트 0). 기능은 그대로 두고 외형만 페이지 문법으로 옮긴다.
 *
 * - 사각 48 · `lineSoft` 1px · radius 2(블랙 CTA 와 같은 값) · 그림자 없음 · 글자 없음
 * - 아이콘은 Lucide `arrow-up`(ISC) 원본 경로, strokeWidth 1 (DESIGN_SYSTEM §9)
 * - 나타날 때 opacity 를 쓰지 않는다 — §9 모션은 color·transform·background 만. 아래에서 8px 올라오고,
 *   숨을 때는 transform 이 끝난 뒤 visibility 를 끈다(보이지 않는 버튼이 클릭·포커스를 먹지 않게)
 * - 숨은 동안 `tabIndex=-1` · `aria-hidden` — 키보드 순서에서 빠진다
 */
export const MonolithGoTop: React.FC<PropsType> = ({ show, onClick }) => (
	<button
		type="button"
		css={style}
		className={show ? 'on' : ''}
		onClick={onClick}
		aria-label="Back to top"
		aria-hidden={!show}
		tabIndex={show ? 0 : -1}
	>
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={20}
			height={20}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="m5 12 7-7 7 7" />
			<path d="M12 19V5" />
		</svg>
	</button>
)

const style = css({
	position: 'fixed',
	right: 'clamp(16px, 2.4vw, 32px)',
	bottom: 'calc(clamp(16px, 2.4vw, 32px) + env(safe-area-inset-bottom, 0px))',
	zIndex: 4,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: 48,
	height: 48,
	padding: 0,
	margin: 0,
	boxSizing: 'border-box',
	background: mono.bg,
	color: mono.ink,
	border: `1px solid ${mono.lineSoft}`,
	borderRadius: 2,
	cursor: 'pointer',

	visibility: 'hidden',
	transform: 'translateY(8px)',
	transition:
		'transform 200ms ease-out, border-color 200ms ease-out, background-color 200ms ease-out, visibility 0s linear 200ms',

	'&.on': {
		visibility: 'visible',
		transform: 'translateY(0)',
		transition:
			'transform 200ms ease-out, border-color 200ms ease-out, background-color 200ms ease-out, visibility 0s',
	},

	'&:hover': {
		borderColor: mono.ink,
		background: mono.surface,
	},

	'&:focus-visible': {
		outline: `1px solid ${mono.ink}`,
		outlineOffset: 3,
	},

	svg: {
		display: 'block',
	},

	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
		'&.on': { transition: 'none' },
	},

	'@media print': {
		display: 'none',
	},
})
